import { Link } from "react-router-dom";
import {
    User2, Briefcase, Eye, Phone, Mail, MessageCircle, Users as UsersIcon, Video, MapPin,
    Send as SendIcon, Clock, Download,
} from "lucide-react";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const logContact = (cid, ch) => { api.post("/audit/contact", { candidate_id: cid, channel: ch }).catch(() => {}); };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

/**
 * SmartInterviewRow — rich calendar card showing candidate, job, interviewers,
 * recruiter, and quick actions (preview, call, WA, email, start meeting).
 * Used in calendar list, schedule and day views as well as the date-side panel.
 *
 * Pass `variant` to choose between "row" (full-width horizontal) and
 * "card" (vertical, fits in a side panel).
 */
export default function SmartInterviewRow({ e, onOpen, onPreviewCandidate, variant = "row" }) {
    const digits = (e.candidate?.phone || "").replace(/\D/g, "");
    const isCard = variant === "card";

    const Header = (
        <div className={isCard ? "flex items-center justify-between gap-2" : "flex items-center gap-2 flex-wrap"}>
            <button type="button" onClick={() => onOpen?.(e)} className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline text-left">
                {fmtTime(e.scheduled_at)} · {e.round_name || `Round ${e.round_number || 1}`}
            </button>
            <span className={`text-[10px] font-mono uppercase tracking-widest border ${e.is_placeholder ? "bg-amber-100 text-amber-800 border-amber-300" : "border-border bg-secondary"} px-1.5 py-0.5 inline-flex items-center gap-1`}>
                {e.online_offline === "online" || e.mode === "Video" ? <><Video className="w-3 h-3" /> Online</> : <><MapPin className="w-3 h-3" /> Offline</>}
            </span>
        </div>
    );

    const Body = (
        <div className={isCard ? "mt-2" : "flex-1 min-w-[260px]"}>
            <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => onOpen?.(e)} className="font-medium text-sm inline-flex items-center gap-1.5 hover:text-ikb">
                    <User2 className="w-3.5 h-3.5" /> {e.candidate?.name || (e.is_placeholder ? "Unattached slot" : "Candidate")}
                </button>
                {e.candidate_id && onPreviewCandidate && (
                    <button type="button" onClick={() => onPreviewCandidate(e.candidate_id)} className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-1 py-0.5 hover:bg-secondary inline-flex items-center gap-0.5" data-testid={`sir-cand-preview-${e.id}`} title="Quick preview">
                        <Eye className="w-3 h-3" />
                    </button>
                )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap text-xs text-muted-foreground">
                {e.job?.title ? (
                    <>
                        <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {e.job.title}</span>
                        <Link to={`/jobs/${e.job_id}`} target="_blank" className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-1 py-0.5 hover:bg-secondary inline-flex items-center gap-0.5" data-testid={`sir-job-preview-${e.id}`} title="Open job"><Eye className="w-3 h-3" /></Link>
                    </>
                ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700"><Briefcase className="w-3 h-3" /> No job assigned</span>
                )}
                {e.created_by_name && <><span>·</span><span className="inline-flex items-center gap-1 text-foreground/70">by {e.created_by_name}</span></>}
                {e.duration_minutes && <><span>·</span><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {e.duration_minutes}m</span></>}
            </div>
            {(e.interviewer_names || []).length > 0 && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <UsersIcon className="w-3 h-3 text-foreground/50" />
                    {(e.interviewer_names || []).slice(0, 4).map((n, idx) => (
                        <span key={idx} className="text-[10px] bg-secondary border border-border px-1.5 py-0.5" data-testid={`sir-iv-chip-${e.id}-${idx}`}>{n}</span>
                    ))}
                    {(e.interviewer_names || []).length > 4 && <span className="text-[10px] text-foreground/60">+{(e.interviewer_names || []).length - 4}</span>}
                </div>
            )}
        </div>
    );

    const Actions = (
        <div className={`flex items-center gap-1.5 flex-wrap ${isCard ? "mt-2" : ""}`}>
            {e.candidate_id && (
                <>
                    {e.candidate?.phone ? (
                        <>
                            <a href={`tel:${e.candidate.phone}`} onClick={() => logContact(e.candidate_id, "call")} className="border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 p-1.5 inline-flex" title={`Call ${e.candidate.phone}`} data-testid={`sir-call-${e.id}`}>
                                <Phone className="w-3.5 h-3.5" />
                            </a>
                            <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" onClick={() => logContact(e.candidate_id, "whatsapp")} className="border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 p-1.5 inline-flex" title="WhatsApp" data-testid={`sir-wa-${e.id}`}>
                                <MessageCircle className="w-3.5 h-3.5" />
                            </a>
                        </>
                    ) : (
                        <>
                            <span className="border border-border bg-secondary text-foreground/40 p-1.5 inline-flex cursor-not-allowed" title="No phone on file">
                                <Phone className="w-3.5 h-3.5" />
                            </span>
                            <span className="border border-border bg-secondary text-foreground/40 p-1.5 inline-flex cursor-not-allowed" title="No phone on file">
                                <MessageCircle className="w-3.5 h-3.5" />
                            </span>
                        </>
                    )}
                    {e.candidate?.email ? (
                        <a href={`mailto:${e.candidate.email}`} onClick={() => logContact(e.candidate_id, "email")} className="border border-ikb bg-blue-50 text-ikb hover:bg-blue-100 p-1.5 inline-flex" title={`Email ${e.candidate.email}`} data-testid={`sir-email-${e.id}`}>
                            <Mail className="w-3.5 h-3.5" />
                        </a>
                    ) : (
                        <span className="border border-border bg-secondary text-foreground/40 p-1.5 inline-flex cursor-not-allowed" title="No email on file">
                            <Mail className="w-3.5 h-3.5" />
                        </span>
                    )}
                </>
            )}
            {e.meet_link && (
                <a href={e.meet_link} target="_blank" rel="noreferrer" className="border border-ikb bg-ikb text-white hover:bg-[#001f7a] px-2 py-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest" title="Start meeting" data-testid={`sir-start-${e.id}`}>
                    <Video className="w-3 h-3" /> Start
                </a>
            )}
            {e.candidate_id && e.id && e.scheduled_at && (
                <a
                    href={`${API}/candidates/${e.candidate_id}/interviews/${e.id}/invite.ics`}
                    className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 px-2 py-1.5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
                    title="Download .ics — hand to candidate before the automated email arrives"
                    data-testid={`sir-ics-${e.id}`}
                >
                    <Download className="w-3 h-3" /> .ics
                </a>
            )}
            {e.invite_sent_at && <span className="text-[10px] font-mono inline-flex items-center gap-1 text-emerald-700"><SendIcon className="w-3 h-3" /> Sent</span>}
        </div>
    );

    if (isCard) {
        return (
            <div className="border border-border bg-white p-3" data-testid={`sir-card-${e.id}`}>
                {Header}
                {Body}
                {Actions}
            </div>
        );
    }

    return (
        <div className="p-3 sm:p-4 hover:bg-secondary/30 flex items-center gap-4 flex-wrap" data-testid={`sir-row-${e.id}`}>
            <button type="button" onClick={() => onOpen?.(e)} className="text-xs font-mono uppercase tracking-widest text-ikb min-w-[150px] text-left hover:underline">
                {new Date(e.scheduled_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                <div className="text-foreground/80">{fmtTime(e.scheduled_at)}</div>
            </button>
            {Body}
            {Actions}
        </div>
    );
}
